import { a as __rest } from '../../tslib.es6.esm.js';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { simulateButtonKeyboardBehavior } from '../../utils/buttonKeyboardBehavior.esm.js';
import { Spinner } from '../Spinner/Spinner.esm.js';
import buttonStyles from './Button.styles.esm.js';

/**
 * Button component communicates an action will occur, like advancing to the next page, or saving information. Button labels clearly describe the action they trigger.
 */
const Button = forwardRef((_a, ref) => {
    var { variant = "primary", size = "md", className = "", children, onClick, inverse = false, disabled = false, isLoading = false, loadingMessage = "Loading", icon, iconPosition = "left", href } = _a, props = __rest(_a, ["variant", "size", "className", "children", "onClick", "inverse", "disabled", "isLoading", "loadingMessage", "icon", "iconPosition", "href"]);
    const hasHref = Boolean(href);
    const hasIcon = Boolean(icon);
    const isInteractionDisabled = disabled || isLoading;
    const styleOptions = {
        size: typeof size === "string" ? size : (size === null || size === void 0 ? void 0 : size.xs) || undefined,
        smSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.sm : undefined,
        mdSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.md : undefined,
        lgSize: typeof size === "object" ? size === null || size === void 0 ? void 0 : size.lg : undefined,
        variant,
        inverse,
        disabled,
        iconPosition,
        class: className,
        isLoading,
    };
    const { base: baseClasses, hasIcon: iconClasses, spinnerArc, } = buttonStyles(styleOptions);
    const handleClick = (e) => {
        // Prevent all interactions when loading or disabled
        if (isInteractionDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        // Call onClick if provided
        onClick === null || onClick === void 0 ? void 0 : onClick(e);
    };
    const Component = href ? "a" : "button";
    const componentProps = Object.assign(Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, props), { ref, className: "iris:font-(family-name:--comp-type-font-button) " +
            baseClasses({ class: className }), onClick: handleClick }), (isInteractionDisabled && {
        "aria-disabled": true,
        "aria-live": "polite",
    })), (isLoading && { "aria-label": `${loadingMessage}` })), (!hasHref && { disabled: isInteractionDisabled })), (hasHref && Object.assign({}, (isInteractionDisabled
        ? { href: undefined, role: "link" }
        : { href, onKeyDown: simulateButtonKeyboardBehavior(onClick) }))));
    const renderContent = () => {
        if (isLoading) {
            return (jsx(Spinner, { size: size, inverse: false, arcClassName: spinnerArc(), message: loadingMessage, "aria-hidden": true }));
        }
        if (!hasIcon)
            return children;
        const iconElement = jsx("span", { className: iconClasses(), children: icon });
        return iconPosition === "left" ? (jsxs(Fragment, { children: [iconElement, children] })) : (jsxs(Fragment, { children: [children, iconElement] }));
    };
    return jsx(Component, Object.assign({}, componentProps, { children: renderContent() }));
});
Button.displayName = "Button";

export { Button };
